import React from "react";
import { Building2 } from "lucide-react";

export default function PropertyAutomationPicker({
  properties,
  includedIds,
  onChange,
  label = "Properties to include",
  helperText = "Only checked properties will receive automated reminders.",
}) {
  const included = new Set(includedIds || []);

  const toggle = (propertyId) => {
    const next = new Set(included);
    if (next.has(propertyId)) {
      next.delete(propertyId);
    } else {
      next.add(propertyId);
    }
    onChange(Array.from(next));
  };

  const setAll = (selectAll) => {
    onChange(selectAll ? properties.map((p) => p.id) : []);
  };

  if (!properties.length) {
    return (
      <div className="sm:col-span-2">
        <label className="label-base">{label}</label>
        <div className="rounded-lg border border-dashed border-gray-200 dark:border-gray-700 px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
          You don't have any properties yet. Add a property to choose which
          ones receive automated reminders.
        </div>
      </div>
    );
  }

  const allSelected = included.size === properties.length;
  const noneSelected = included.size === 0;

  return (
    <div className="sm:col-span-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="label-base">{label}</label>
        <div className="flex gap-3 text-xs">
          <button
            type="button"
            onClick={() => setAll(true)}
            disabled={allSelected}
            className="font-medium text-indigo-600 hover:text-indigo-700 disabled:cursor-default disabled:text-gray-400"
          >
            Select all
          </button>
          <button
            type="button"
            onClick={() => setAll(false)}
            disabled={noneSelected}
            className="font-medium text-indigo-600 hover:text-indigo-700 disabled:cursor-default disabled:text-gray-400"
          >
            Select none
          </button>
        </div>
      </div>
      <div className="mt-2 max-h-48 space-y-1 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 p-2">
        {properties.map((p) => {
          const checked = included.has(p.id);
          return (
            <label
              key={p.id}
              className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(p.id)}
                className="checkbox-base"
              />
              <Building2 className="h-3.5 w-3.5 text-gray-400" />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {p.name}
              </span>
            </label>
          );
        })}
      </div>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{helperText}</p>
      {noneSelected && (
        <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
          No properties selected. Automation will not run for any tenants.
        </p>
      )}
    </div>
  );
}
